import { createTheme } from '@mui/material/styles';

// Sophia Design Tokens
const sophia = {
  primary: '#D0DF00', // bright green — highlights only, sparingly
  accentPurple: '#250e62', // main brand color — links, buttons, headings
  accentLightBlue: '#8BD3E6',
  white: '#fff',
  lightWarmGrey: '#f5f5f5',
  lightCoolGrey: '#EFF3F5', // header block backgrounds
  outline: '#e6e6e6',
  mediumGrey: '#C5CDD2', // table headers
  grey: '#98A4AE',
  darkGrey: '#6D6E71',
  darkestGrey: '#444',
  black: '#000',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: sophia.accentPurple,
      contrastText: sophia.white,
    },
    secondary: {
      main: sophia.primary,
      contrastText: sophia.accentPurple,
    },
    background: {
      default: sophia.lightCoolGrey,
      paper: sophia.white,
    },
    text: {
      primary: sophia.darkestGrey,
      secondary: sophia.darkGrey,
    },
    divider: sophia.outline,
  },
  typography: {
    fontFamily:
      '"Jost", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 15,
    h1: { fontWeight: 600, fontSize: '2.125rem' },
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: sophia.white,
          color: sophia.accentPurple,
          borderBottom: `1px solid ${sophia.outline}`,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: sophia.accentPurple,
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '28.5px',
          textTransform: 'none',
          fontWeight: 600,
        },
        sizeLarge: {
          padding: '13px 30px 16px 30px',
        },
        sizeMedium: {
          padding: '8px 24px 10px 24px',
        },
        sizeSmall: {
          padding: '3px 20px 2px 20px',
        },
        // "contained" = secondary button (purple) — the default/most used
        contained: {
          backgroundColor: sophia.accentPurple,
          color: sophia.white,
          border: `2px solid ${sophia.accentPurple}`,
          '&:hover': {
            backgroundColor: sophia.darkGrey,
            borderColor: sophia.darkGrey,
          },
        },
        // "outlined" = quaternary style (white bg, purple border)
        outlined: {
          backgroundColor: sophia.white,
          color: sophia.accentPurple,
          borderWidth: 2,
          borderColor: sophia.accentPurple,
          '&:hover': {
            backgroundColor: sophia.darkGrey,
            borderColor: sophia.darkGrey,
            borderWidth: 2,
            color: sophia.white,
          },
        },
        // "text" button keeps purple text
        text: {
          color: sophia.accentPurple,
          fontWeight: 700,
          '&:hover': {
            backgroundColor: sophia.lightCoolGrey,
          },
        },
      },
    },
    // Green highlight button: use color="secondary" variant="contained"
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: sophia.mediumGrey,
          '& .MuiTableCell-head': {
            backgroundColor: sophia.mediumGrey,
            color: sophia.darkestGrey,
            fontWeight: 700,
            fontSize: '0.8125rem',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s ease',
          '&.MuiTableRow-hover:hover': {
            backgroundColor: sophia.lightCoolGrey,
          },
          '&.Mui-selected': {
            backgroundColor: sophia.lightCoolGrey,
            '&:hover': {
              backgroundColor: sophia.lightCoolGrey,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: sophia.lightWarmGrey,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          transition: 'color 0.3s ease',
          '&:hover': {
            color: sophia.accentPurple,
          },
          '&.Mui-active': {
            color: sophia.accentPurple,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s ease',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'color 0.3s ease, background-color 0.3s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '28.5px',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: `1px solid ${sophia.outline}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: sophia.white,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: sophia.white,
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border-color 0.3s ease',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: sophia.accentPurple,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: sophia.accentPurple,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: sophia.white,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          borderRadius: 4,
          backgroundColor: sophia.white,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: sophia.grey,
          '&.Mui-checked': {
            color: sophia.accentPurple,
          },
        },
      },
    },
  } as Record<string, unknown>,
});

// MUI X DatePicker component overrides (applied at runtime, not in MUI core types)
const themeWithPickers = {
  ...theme,
  components: {
    ...theme.components,
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: sophia.white,
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: sophia.white,
          '& .MuiPickersOutlinedInput-notchedOutline': {
            transition: 'border-color 0.3s ease',
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: sophia.accentPurple,
          },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiPickersOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: sophia.white,
          },
        },
      },
    },
  },
};

export default themeWithPickers;
